import { Badge, Button, cardClass, fieldClass, SearchField, selectClass, Table, textareaClass } from '@ledgerloom/ui';
import { useState } from 'react';
import { propose } from '../api';
import { useApi, useDebounced, usePageTitle, useWorkspaceCurrency } from '../hooks';
import { useSession } from '../session';
import { Empty, ErrorBanner, Icon, ListSkeleton, outcomeMessage, outcomeTone, Time, ToggleGroup, useToast, badgeTone } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { money, toMinor } from '../format';

// Leads: who came in, how they scored, who owns them, and converting one to a
// contact and a deal. Patterns: data-display/table, forms/search-field, and a
// modal for a new lead.

interface Lead { id: string; title: string; status: string; score: number | null; grade: string | null; owner_name: string | null; source: { channel?: string; detail?: string }; contact: Record<string, string>; value_minor: string | null; currency: string | null; created_at: string; version: number }

export function Leads() {
  const [status, setStatus] = useState<'open' | 'converted' | 'disqualified'>('open');
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const leads = useApi<Lead[]>(`/v1/leads?status=${status}&limit=200${dq ? `&q=${encodeURIComponent(dq)}` : ''}`, [status, dq]);
  const [creating, setCreating] = useState(false);
  const [converting, setConverting] = useState<Lead | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const { refreshStats } = useSession();
  usePageTitle('Leads');

  async function convert() {
    const l = converting!;
    setBusy(true);
    try {
      const out = await propose('lead.convert', { lead_id: l.id, base_version: l.version, deal: { title: l.title }, reason: 'Qualified' });
      toast(outcomeMessage(out, `Converted ${l.title}`), outcomeTone(out));
      setConverting(null); refreshStats(); void leads.reload();
    } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(false); }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Leads</h1><p>Scored by the workspace's score model and routed to an owner as they arrive.</p></div>
        <Button type="submit" onClick={() => setCreating(true)}><Icon name="plus" />New lead</Button>
      </div>
      <div className="row">
        <SearchField id="lead-q" className="grow basis-56 max-w-[360px]" label="Search leads" placeholder="Search by name, email, or company" value={q} onValueChange={setQ} />
        <ToggleGroup label="Show leads that are" value={status} options={[['open', 'Open'], ['converted', 'Converted'], ['disqualified', 'Disqualified']]} onChange={setStatus} />
      </div>
      <ErrorBanner error={leads.error} />
      <section className={cardClass}>
        {leads.loading && !leads.data ? <ListSkeleton rows={5} label="Loading leads" /> : !leads.data?.length ? <Empty title={`No ${status} leads`} /> : (
          <Table label="Leads">
            <caption className="sr-only">{status} leads</caption>
            <thead><tr><th scope="col">Lead</th><th scope="col">Source</th><th scope="col">Score</th><th scope="col" className="hide-sm">Owner</th><th scope="col" className="hide-sm">Value</th><th scope="col" className="hide-sm">Arrived</th>{status === 'open' && <th scope="col"><span className="sr-only">Actions</span></th>}</tr></thead>
            <tbody>{leads.data.map((l) => (
              <tr key={l.id}>
                <td>{l.title}<div className="faint">{[l.contact.email, l.contact.company].filter(Boolean).join(' · ')}</div></td>
                <td className="small">{l.source.channel}{l.source.detail ? ` · ${l.source.detail}` : ''}</td>
                <td>{l.grade ? <Badge tone={badgeTone(l.grade === 'A' ? 'ok' : l.grade === 'B' ? 'info' : '')}>{l.grade}</Badge> : null} {l.score ?? '–'}</td>
                <td className="small hide-sm">{l.owner_name ?? 'Unassigned'}</td>
                <td className="small hide-sm">{l.value_minor ? money(l.value_minor, l.currency ?? 'USD', { cents: false }) : '–'}</td>
                <td className="small hide-sm"><Time iso={l.created_at} /></td>
                {status === 'open' && <td><Button type="submit" variant="secondary" size="sm" onClick={() => setConverting(l)}>Convert<span className="sr-only"> {l.title}</span>…</Button></td>}
              </tr>
            ))}</tbody>
          </Table>
        )}
      </section>
      {creating && <NewLead onClose={() => setCreating(false)} onDone={() => { setCreating(false); void leads.reload(); }} />}
      <ConfirmDialog open={!!converting} title={`Convert ${converting?.title ?? ''}?`} confirmLabel="Convert to a deal" busy={busy} onConfirm={convert} onCancel={() => setConverting(null)}>
        <p style={{ margin: 0 }}>The lead becomes a contact (or joins the matching one) and an open deal in the first stage. The lead is kept with the status converted.</p>
      </ConfirmDialog>
    </div>
  );
}

function NewLead({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ given_name: '', family_name: '', email: '', company: '', phone: '', message: '', channel: 'manual', value: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const currency = useWorkspaceCurrency();
  async function save() {
    if (!currency) { setError(new Error('The workspace currency is still loading. Try again.')); return; }
    setBusy(true); setError(null);
    try {
      const contact = Object.fromEntries(Object.entries({ given_name: f.given_name, family_name: f.family_name, email: f.email, company: f.company, phone: f.phone, message: f.message }).filter(([, v]) => v));
      const out = await propose('lead.create', { contact, source: { channel: f.channel }, ...(f.value ? { value: { minor: toMinor(f.value), scale: 4, currency } } : {}) });
      if (out.state === 'rejected') { setError(new Error(outcomeMessage(out))); return; }
      toast(outcomeMessage(out, `Lead saved${out.result?.grade ? `, grade ${out.result.grade}` : ''}`), outcomeTone(out));
      onDone();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  return (
    <ConfirmDialog open title="New lead" confirmLabel="Save lead" busy={busy} onConfirm={save} onCancel={onClose}>
      <ErrorBanner error={error} />
      <div className="form-grid">
        <Field label="First name"><input className={fieldClass} value={f.given_name} onChange={set('given_name')} /></Field>
        <Field label="Last name"><input className={fieldClass} value={f.family_name} onChange={set('family_name')} /></Field>
        <Field label="Email"><input className={fieldClass} type="email" value={f.email} onChange={set('email')} /></Field>
        <Field label="Phone"><input className={fieldClass} type="tel" value={f.phone} onChange={set('phone')} /></Field>
        <Field label="Company"><input className={fieldClass} value={f.company} onChange={set('company')} /></Field>
        <Field label="Source"><select className={selectClass} value={f.channel} onChange={set('channel')}>{['manual', 'referral', 'form', 'email', 'event', 'webhook'].map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
        <Field label={`Value (${currency ?? '…'})`}><input className={fieldClass} inputMode="decimal" value={f.value} onChange={set('value')} /></Field>
        <Field label="Message" className="full"><textarea className={textareaClass} value={f.message} onChange={set('message')} /></Field>
      </div>
    </ConfirmDialog>
  );
}
